import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { CheckCircle2, HardHat, Loader2, Camera, Upload, Eye, ZoomIn, ZoomOut, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import apiClient from "../services/api";
import { formatPhoneNumber, formatDateInput, formatSSN } from "../lib/formatters";
import SignaturePad from "../components/SignaturePad";

const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];

const todayStr = () => {
  const d = new Date();
  return `${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}/${d.getFullYear()}`;
};

const Section = ({ title, children, testId }) => (
  <div className="border-t pt-6 mt-6 first:border-t-0 first:pt-0 first:mt-0" data-testid={testId}>
    <h2 className="text-lg font-semibold text-slate-900 mb-4">{title}</h2>
    <div className="space-y-4">{children}</div>
  </div>
);

const Field = ({ label, children, required }) => (
  <div>
    <Label className="text-sm text-slate-700">{label}{required && " *"}</Label>
    <div className="mt-1">{children}</div>
  </div>
);

const OnboardingForm = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const isLoggedIn = !!sessionStorage.getItem("token");
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [savedDraft, setSavedDraft] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [company, setCompany] = useState({ name: "V2R Masonry Systems" });

  const [f, setF] = useState({
    first_name: "", last_name: "", middle_initial: "",
    street: "", apt: "", city: "", state: "", zip: "",
    phone: "", email: "", dob: "", dob2: "", ssn: "", ssn2: "",
    ec_name: "", ec_relationship: "", ec_phone: "",
    prev1: "", prev2: "", prev3: "", train1: "", train2: "", train3: "",
    citizenship_status: "", uscis_number: "", work_auth_exp: "",
    filing_status: "", multiple_jobs: false,
    dependents_under_17: "", dependents_over_17: "", other_income: "", deductions: "", extra_withholding: "",
    bank_name: "", account_name: "", account_number: "", account_number2: "",
    routing_number: "", routing_number2: "",
  });
  const [signature, setSignature] = useState("");
  const [idImages, setIdImages] = useState({});
  const [uploadingId, setUploadingId] = useState("");
  const [viewedPaySchedule, setViewedPaySchedule] = useState(false);
  const [psOpen, setPsOpen] = useState(false);
  const [psImg, setPsImg] = useState("");
  const [psZoom, setPsZoom] = useState(1);
  const dlRef = useRef(null);
  const ssnCardRef = useRef(null);

  const openPaySchedule = async () => {
    setPsOpen(true);
    setViewedPaySchedule(true);
    if (!psImg) {
      try {
        const res = await apiClient.get(`/onboarding/pay-schedule/preview.png`, { responseType: "blob" });
        setPsImg(URL.createObjectURL(res.data));
      } catch {
        toast.error("Could not load the Pay Schedule. Try 'Open as PDF' instead.");
      }
    }
  };

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    (async () => {
      try {
        const { data } = await apiClient.get(`/onboarding/token/${token}`);
        if (data.status === "completed") { setSubmitted(true); }
        setCompany(data.company || {});
        const imgMap = {};
        (data.id_images || []).forEach((i) => { imgMap[i.label] = i; });
        setIdImages(imgMap);
        setF((p) => ({
          ...p,
          first_name: data.prefill?.first_name || "",
          last_name: data.prefill?.last_name || "",
          phone: data.prefill?.phone || "",
          email: data.prefill?.email || "",
          ...(data.draft || {}),
        }));
      } catch (e) {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const validate = () => {
    const required = [
      ["first_name", "First name"], ["last_name", "Last name"], ["street", "Street address"],
      ["city", "City"], ["state", "State"], ["zip", "ZIP code"], ["dob", "Date of birth"],
      ["ssn", "Social Security Number"], ["ec_name", "Emergency contact name"],
      ["citizenship_status", "Citizenship status"], ["filing_status", "W-4 filing status"],
      ["bank_name", "Bank name"], ["account_name", "Name on account"],
      ["account_number", "Account number"], ["routing_number", "Routing number"],
    ];
    for (const [k, lbl] of required) {
      if (!String(f[k]).trim()) { toast.error(`${lbl} is required.`); return false; }
    }
    if (f.ssn !== f.ssn2) { toast.error("Social Security Numbers do not match."); return false; }
    if (f.dob !== f.dob2) { toast.error("Dates of birth do not match."); return false; }
    if (f.account_number !== f.account_number2) { toast.error("Account numbers do not match."); return false; }
    if (f.routing_number !== f.routing_number2) { toast.error("Routing numbers do not match."); return false; }
    if (!signature) { toast.error("Please sign before submitting."); return false; }
    if (!viewedPaySchedule) { toast.error("Please review the Pay Schedule before submitting."); return false; }
    return true;
  };

  const handleIdUpload = async (label, file) => {
    if (!file) return;
    setUploadingId(label);
    const fd = new FormData();
    fd.append("label", label);
    fd.append("file", file);
    try {
      const { data } = await apiClient.post(`/onboarding/token/${token}/id-image`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setIdImages((p) => ({ ...p, [label]: { id: data.id, label, filename: data.filename } }));
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed. Please try again.");
    } finally {
      setUploadingId("");
    }
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    const payload = {
      first_name: f.first_name, last_name: f.last_name, middle_initial: f.middle_initial,
      address: { street: f.street, apt: f.apt, city: f.city, state: f.state, zip: f.zip },
      phone: f.phone, email: f.email, dob: f.dob, ssn: f.ssn,
      emergency_contact: { name: f.ec_name, relationship: f.ec_relationship, phone: f.ec_phone },
      previous_employers: [f.prev1, f.prev2, f.prev3].filter(Boolean),
      training: [f.train1, f.train2, f.train3].filter(Boolean),
      citizenship_status: f.citizenship_status, uscis_number: f.uscis_number, work_auth_exp: f.work_auth_exp,
      filing_status: f.filing_status, multiple_jobs: f.multiple_jobs,
      dependents_under_17: f.dependents_under_17, dependents_over_17: f.dependents_over_17, other_income: f.other_income,
      deductions: f.deductions, extra_withholding: f.extra_withholding,
      bank_name: f.bank_name, account_name: f.account_name,
      account_number: f.account_number, routing_number: f.routing_number,
      signature, signature_date: todayStr(),
    };
    try {
      await apiClient.post(`/onboarding/token/${token}/submit`, payload);
      setSubmitted(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveDraft = async () => {
    setSubmitting(true);
    try {
      await apiClient.post(`/onboarding/token/${token}/draft`, { data: f });
      setSavedDraft(true);
    } catch (err) {
      toast.error("Could not save your progress. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  }
  if (notFound) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4"><div className="max-w-md text-center"><h1 className="text-xl font-bold text-slate-900">Link not found</h1><p className="text-slate-600 mt-2">This onboarding link is invalid or has expired. Please contact V2R Masonry.</p></div></div>;
  }
  if (savedDraft) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center" data-testid="onboarding-draft-saved">
          <CheckCircle2 className="w-14 h-14 text-blue-600 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900">Progress Saved</h1>
          <p className="mt-2 text-slate-600">
            Your paperwork has been saved but not yet submitted. You can return to
            <strong> this same link</strong> anytime to finish. Nothing is sent to {company.name} until you submit.
          </p>
          <div className="mt-5 flex flex-col sm:flex-row gap-2 justify-center">
            <Button variant="outline" onClick={() => setSavedDraft(false)} data-testid="draft-resume-now">Keep Editing</Button>
            {isLoggedIn && (
              <Button onClick={() => navigate("/applications")} data-testid="draft-back-applications">Back to Applications</Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center" data-testid="onboarding-success">
          <CheckCircle2 className="w-14 h-14 text-emerald-600 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900">Paperwork Submitted</h1>
          <p className="mt-2 text-slate-600">Thank you! Your new-hire forms have been sent to {company.name} for final review.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-slate-900 text-white mb-3"><HardHat className="w-7 h-7" /></div>
          <h1 className="text-3xl font-bold text-slate-900">New-Hire Onboarding</h1>
          <p className="mt-2 text-slate-600">Complete your paperwork for {company.name}. You'll only enter each detail once.</p>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-6" data-testid="onboarding-form">
          <Section title="Personal Information" testId="section-personal">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Field label="First Name" required><Input value={f.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="ob-first-name" /></Field>
              <Field label="Middle Initial"><Input value={f.middle_initial} maxLength={1} onChange={(e) => set("middle_initial", e.target.value)} data-testid="ob-middle-initial" /></Field>
              <Field label="Last Name" required><Input value={f.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="ob-last-name" /></Field>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Field label="Street Address" required><Input value={f.street} onChange={(e) => set("street", e.target.value)} data-testid="ob-street" /></Field>
              <Field label="Apt/Unit"><Input value={f.apt} onChange={(e) => set("apt", e.target.value)} data-testid="ob-apt" /></Field>
              <Field label="City" required><Input value={f.city} onChange={(e) => set("city", e.target.value)} data-testid="ob-city" /></Field>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Field label="State" required>
                <Select value={f.state} onValueChange={(v) => set("state", v)}>
                  <SelectTrigger data-testid="ob-state"><SelectValue placeholder="State" /></SelectTrigger>
                  <SelectContent>{US_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="ZIP Code" required><Input value={f.zip} onChange={(e) => set("zip", e.target.value)} data-testid="ob-zip" /></Field>
              <Field label="Phone"><Input value={f.phone} onChange={(e) => set("phone", formatPhoneNumber(e.target.value))} data-testid="ob-phone" /></Field>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Field label="Email"><Input type="email" value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="ob-email" /></Field>
              <Field label="Date of Birth" required><Input placeholder="MM/DD/YYYY" value={f.dob} onChange={(e) => set("dob", formatDateInput(e.target.value))} data-testid="ob-dob" /></Field>
              <Field label="Confirm Date of Birth" required><Input placeholder="MM/DD/YYYY" value={f.dob2} onChange={(e) => set("dob2", formatDateInput(e.target.value))} data-testid="ob-dob2" /></Field>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Field label="Social Security #" required><Input placeholder="123-45-6789" value={f.ssn} onChange={(e) => set("ssn", formatSSN(e.target.value))} data-testid="ob-ssn" /></Field>
              <Field label="Confirm Social Security #" required><Input placeholder="123-45-6789" value={f.ssn2} onChange={(e) => set("ssn2", formatSSN(e.target.value))} data-testid="ob-ssn2" /></Field>
            </div>
            <p className="text-xs text-slate-500">Your SSN and date of birth are used only to generate your official forms and are never stored on their own.</p>
          </Section>

          <Section title="Emergency Contact" testId="section-emergency">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Field label="Name" required><Input value={f.ec_name} onChange={(e) => set("ec_name", e.target.value)} data-testid="ob-ec-name" /></Field>
              <Field label="Relationship"><Input value={f.ec_relationship} onChange={(e) => set("ec_relationship", e.target.value)} data-testid="ob-ec-relationship" /></Field>
              <Field label="Phone Number"><Input type="tel" placeholder="(254) 555-1234" value={f.ec_phone} onChange={(e) => set("ec_phone", formatPhoneNumber(e.target.value))} data-testid="ob-ec-phone" /></Field>
            </div>
          </Section>

          <Section title="Previous Employers" testId="section-prev">
            <Input placeholder="Employer 1 (name, dates, position)" value={f.prev1} onChange={(e) => set("prev1", e.target.value)} data-testid="ob-prev1" />
            <Input placeholder="Employer 2" value={f.prev2} onChange={(e) => set("prev2", e.target.value)} data-testid="ob-prev2" />
            <Input placeholder="Employer 3" value={f.prev3} onChange={(e) => set("prev3", e.target.value)} data-testid="ob-prev3" />
          </Section>

          <Section title="Training & Certificates" testId="section-training">
            <Input placeholder="Training/Certificate 1" value={f.train1} onChange={(e) => set("train1", e.target.value)} data-testid="ob-train1" />
            <Input placeholder="Training/Certificate 2" value={f.train2} onChange={(e) => set("train2", e.target.value)} data-testid="ob-train2" />
            <Input placeholder="Training/Certificate 3" value={f.train3} onChange={(e) => set("train3", e.target.value)} data-testid="ob-train3" />
          </Section>

          <Section title="I-9: Citizenship / Work Authorization" testId="section-i9">
            <RadioGroup value={f.citizenship_status} onValueChange={(v) => set("citizenship_status", v)}>
              {[["citizen", "A citizen of the United States"], ["noncitizen_national", "A noncitizen national of the United States"], ["lpr", "A lawful permanent resident"], ["alien", "An alien authorized to work"]].map(([val, lbl]) => (
                <div key={val} className="flex items-center gap-2">
                  <RadioGroupItem value={val} id={`cit-${val}`} data-testid={`ob-citizenship-${val}`} />
                  <Label htmlFor={`cit-${val}`} className="text-sm font-normal">{lbl}</Label>
                </div>
              ))}
            </RadioGroup>
            {(f.citizenship_status === "lpr" || f.citizenship_status === "alien") && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="USCIS / A-Number"><Input value={f.uscis_number} onChange={(e) => set("uscis_number", e.target.value)} data-testid="ob-uscis" /></Field>
                {f.citizenship_status === "alien" && <Field label="Work Authorization Expiration"><Input placeholder="MM/DD/YYYY" value={f.work_auth_exp} onChange={(e) => set("work_auth_exp", formatDateInput(e.target.value))} data-testid="ob-workexp" /></Field>}
              </div>
            )}
          </Section>

          <Section title="W-4: Tax Withholding" testId="section-w4">
            <Field label="Filing Status" required>
              <RadioGroup value={f.filing_status} onValueChange={(v) => set("filing_status", v)} className="flex flex-col gap-2">
                {[["single", "Single or Married filing separately"], ["married", "Married filing jointly or Qualifying surviving spouse"], ["head", "Head of household"]].map(([val, lbl]) => (
                  <div key={val} className="flex items-center gap-2"><RadioGroupItem value={val} id={`fs-${val}`} data-testid={`ob-filing-${val}`} /><Label htmlFor={`fs-${val}`} className="text-sm font-normal">{lbl}</Label></div>
                ))}
              </RadioGroup>
            </Field>
            <div className="flex items-center gap-2">
              <Checkbox id="multi" checked={f.multiple_jobs} onCheckedChange={(v) => set("multiple_jobs", !!v)} data-testid="ob-multiple-jobs" />
              <Label htmlFor="multi" className="text-sm font-normal">Multiple jobs or spouse works (Step 2c)</Label>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="# of dependents under age 17"><Input type="number" min="0" value={f.dependents_under_17} onChange={(e) => set("dependents_under_17", e.target.value)} data-testid="ob-dependents-under17" /></Field>
              <Field label="# of other dependents (age 17+)"><Input type="number" min="0" value={f.dependents_over_17} onChange={(e) => set("dependents_over_17", e.target.value)} data-testid="ob-dependents-over17" /></Field>
              <Field label="Other income (4a, $)"><Input value={f.other_income} onChange={(e) => set("other_income", e.target.value)} data-testid="ob-other-income" /></Field>
              <Field label="Deductions (4b, $)"><Input value={f.deductions} onChange={(e) => set("deductions", e.target.value)} data-testid="ob-deductions" /></Field>
              <Field label="Extra withholding (4c, $)"><Input value={f.extra_withholding} onChange={(e) => set("extra_withholding", e.target.value)} data-testid="ob-extra-withholding" /></Field>
            </div>
          </Section>

          <Section title="Direct Deposit" testId="section-dd">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Bank Name" required><Input value={f.bank_name} onChange={(e) => set("bank_name", e.target.value)} data-testid="ob-bank-name" /></Field>
              <Field label="Name as it appears on account" required><Input value={f.account_name} onChange={(e) => set("account_name", e.target.value)} data-testid="ob-account-name" /></Field>
              <Field label="Account Number" required><Input value={f.account_number} onChange={(e) => set("account_number", e.target.value)} data-testid="ob-account-number" /></Field>
              <Field label="Confirm Account Number" required><Input value={f.account_number2} onChange={(e) => set("account_number2", e.target.value)} data-testid="ob-account-number2" /></Field>
              <Field label="Routing Number" required><Input value={f.routing_number} onChange={(e) => set("routing_number", e.target.value)} data-testid="ob-routing-number" /></Field>
              <Field label="Confirm Routing Number" required><Input value={f.routing_number2} onChange={(e) => set("routing_number2", e.target.value)} data-testid="ob-routing-number2" /></Field>
            </div>
          </Section>

          <Section title="Photo ID Upload" testId="section-id-upload">
            <p className="text-sm text-slate-600">
              Please upload a picture of your <strong>Driver's License</strong> and <strong>Social Security card</strong>.
              You can take the photo directly with your phone's camera. This is optional but helps speed up your I-9 verification.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { label: "drivers_license", title: "Driver's License / State ID", ref: dlRef },
                { label: "ssn_card", title: "Social Security Card", ref: ssnCardRef },
              ].map(({ label, title, ref }) => (
                <div key={label} className="border rounded-xl p-4 flex flex-col items-center text-center" data-testid={`id-upload-${label}`}>
                  <p className="text-sm font-medium text-slate-800 mb-2">{title}</p>
                  {idImages[label] ? (
                    <div className="flex items-center gap-1.5 text-emerald-700 text-sm mb-3" data-testid={`id-uploaded-${label}`}>
                      <CheckCircle2 className="w-4 h-4" /> Uploaded
                    </div>
                  ) : (
                    <p className="text-xs text-slate-400 mb-3">No photo yet</p>
                  )}
                  <input
                    ref={ref}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    className="hidden"
                    onChange={(e) => handleIdUpload(label, e.target.files?.[0])}
                    data-testid={`id-input-${label}`}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={uploadingId === label}
                    onClick={() => ref.current?.click()}
                    data-testid={`id-btn-${label}`}
                  >
                    {uploadingId === label ? (
                      <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Uploading...</>
                    ) : idImages[label] ? (
                      <><Upload className="w-4 h-4 mr-1.5" /> Replace Photo</>
                    ) : (
                      <><Camera className="w-4 h-4 mr-1.5" /> Take / Upload Photo</>
                    )}
                  </Button>
                </div>
              ))}
            </div>
          </Section>

          <Section title="Review the Pay Schedule" testId="section-pay-schedule">
            <p className="text-sm text-slate-600">
              Your signature below also acknowledges the company Pay Schedule. Please open and read it
              (pinch or use the zoom buttons to enlarge on your phone) before you sign.
            </p>
            <div className={`flex flex-col sm:flex-row sm:items-center gap-3 rounded-xl border p-4 ${viewedPaySchedule ? "border-emerald-200 bg-emerald-50" : "border-amber-300 bg-amber-50"}`}>
              {viewedPaySchedule ? (
                <span className="flex items-center gap-2 text-sm font-medium text-emerald-700" data-testid="pay-schedule-reviewed">
                  <CheckCircle2 className="w-4 h-4" /> Pay Schedule reviewed
                </span>
              ) : (
                <span className="text-sm font-medium text-amber-800">Required: review before signing</span>
              )}
              <Button type="button" variant={viewedPaySchedule ? "outline" : "default"} size="sm" onClick={openPaySchedule} className="sm:ml-auto" data-testid="pay-schedule-view-btn">
                <Eye className="w-4 h-4 mr-1.5" /> {viewedPaySchedule ? "View Again" : "View Pay Schedule"}
              </Button>
            </div>
          </Section>

          <Section title="Signature" testId="section-signature">
            <p className="text-sm text-slate-600">Your signature below applies to all forms in this packet (Application, I-9, W-4, Direct Deposit, and Pay Schedule acknowledgment). Date: {todayStr()}</p>
            <SignaturePad onChange={setSignature} testId="ob-signature" />
          </Section>

          <div className="flex flex-col sm:flex-row gap-3 mt-6">
            <Button variant="outline" onClick={handleSaveDraft} disabled={submitting} className="h-11 sm:w-56" data-testid="ob-save-draft">
              Save & Finish Later
            </Button>
            <Button onClick={handleSubmit} disabled={submitting || !viewedPaySchedule} className="flex-1 h-11 text-base" data-testid="ob-submit">
              {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</> : !viewedPaySchedule ? "Review Pay Schedule to Continue" : "Submit All Forms"}
            </Button>
          </div>
        </div>
      </div>

      <Dialog open={psOpen} onOpenChange={setPsOpen}>
        <DialogContent className="max-w-3xl" data-testid="pay-schedule-dialog">
          <DialogHeader>
            <DialogTitle>Pay Schedule</DialogTitle>
          </DialogHeader>
          <div className="flex items-center gap-2 mb-2">
            <Button type="button" variant="outline" size="sm" onClick={() => setPsZoom((z) => Math.max(1, +(z - 0.5).toFixed(1)))} data-testid="pay-schedule-zoom-out">
              <ZoomOut className="w-4 h-4" />
            </Button>
            <span className="text-sm text-slate-500 w-12 text-center">{Math.round(psZoom * 100)}%</span>
            <Button type="button" variant="outline" size="sm" onClick={() => setPsZoom((z) => Math.min(3, +(z + 0.5).toFixed(1)))} data-testid="pay-schedule-zoom-in">
              <ZoomIn className="w-4 h-4" />
            </Button>
            <a
              href={`${apiClient.defaults.baseURL}/onboarding/pay-schedule/preview.pdf`}
              target="_blank" rel="noreferrer"
              className="ml-auto inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900"
              data-testid="pay-schedule-open-pdf"
            >
              <ExternalLink className="w-4 h-4" /> Open as PDF
            </a>
          </div>
          <div className="max-h-[70vh] overflow-auto rounded-lg border bg-slate-100" style={{ touchAction: "pinch-zoom" }}>
            {psImg ? (
              <img src={psImg} alt="Pay Schedule" style={{ width: `${psZoom * 100}%`, display: "block" }} data-testid="pay-schedule-image" />
            ) : (
              <div className="h-64 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OnboardingForm;
